import { Component } from '@theme/component';
import { fetchConfig, updateCartCount } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent, CartUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import * as haptic from '@theme/haptic';

/**
 * Safety net for the line removal animation, comfortably above the 300ms declared in
 * `snippets/cart-line-item.liquid`.
 */
const REMOVAL_ANIMATION_TIMEOUT = 500;

class CartItemsComponent extends Component {
  /** @type {Map<number, AbortController>} */
  #abortControllers = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * @param {Event} event
   */
  #onQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const line = event.detail.cartLine;
    if (!line) return;

    this.#updateLine(line, event.detail.quantity);
  };

  /**
   * @param {MouseEvent} event
   */
  removeLine = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const line = Number(event.target.dataset.cartLine);
    if (!line) return;

    haptic.haptic();

    this.#updateLine(line, 0);
  };

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #updateLine(line, quantity) {
    this.#abortControllers.get(line)?.abort();

    const abortController = new AbortController();
    this.#abortControllers.set(line, abortController);

    // A removal already has its own collapse animation, so only a quantity change needs the
    // in-place spinner. The section-wide flag is set in both cases: the recap and the sticky bar
    // keep showing the pre-update amounts until the morph lands.
    if (quantity > 0) this.#lineElement(line)?.classList.add('cart-item--busy');
    this.#setBusy(true);

    // Animate removal before fetching
    const removePromise = quantity === 0
      ? this.#animateRemoval(line)
      : Promise.resolve();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    Promise.all([
      removePromise,
      fetch(Theme.routes.cart_change_url, { ...fetchConfig('json', { body }), signal: abortController.signal }),
    ])
      .then(async ([, res]) => {
        const response = await res.json();

        if (!res.ok) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          this.#showLineError(line, response.description || response.message);
          return;
        }

        updateCartCount(response.item_count);

        this.dispatchEvent(
          new CartUpdateEvent(response, this.sectionId, {
            source: 'cart-items-component',
            sections: response.sections,
            itemCount: response.item_count,
            // `cart/change.js` answers with the whole cart, so listeners that need the new state
            // (Nosto cart tagging) get it without a second request.
            cart: response,
          })
        );

        const html = response.sections?.[this.sectionId];
        if (html) {
          morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        this.#restoreScrollLock();

        if (this.isDrawer && quantity === 0) {
          requestAnimationFrame(() => {
            const title = this.closest('dialog')?.querySelector('.dialog-title');
            if (title instanceof HTMLElement) {
              title.setAttribute('tabindex', '-1');
              title.focus();
            }
          });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        if (this.#abortControllers.get(line) === abortController) {
          this.#abortControllers.delete(line);
        }

        // The morph re-renders the line from server markup, which carries no busy class, so this
        // only really matters on the error and abort paths — and it costs nothing on the others.
        this.#lineElement(line)?.classList.remove('cart-item--busy');
        if (this.#abortControllers.size === 0) this.#setBusy(false);
      });
  }

  /**
   * Resolves the element of a cart line.
   *
   * Never by position: the cart page renders the lines grouped by shipping group (Afibel first,
   * then the LDF brands), so DOM order does not match the cart line order. `data-cart-line` is
   * rendered on every `.cart-item` for exactly that reason.
   *
   * @param {number} line - 1-based line index
   * @returns {HTMLElement | null}
   */
  #lineElement(line) {
    const item = this.querySelector(`.cart-item[data-cart-line="${line}"]`);

    return item instanceof HTMLElement ? item : null;
  }

  /**
   * @param {number} line - 1-based line index
   * @param {string} message
   */
  #showLineError(line, message) {
    const item = this.#lineElement(line);
    if (!item) return;

    // Remove any existing error on this item
    item.querySelector('.cart-item__error')?.remove();

    const error = document.createElement('p');
    error.className = 'cart-item__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;

    // Start collapsed
    error.style.maxHeight = '0';
    error.style.opacity = '0';
    item.appendChild(error);

    // Expand to natural height (includes padding-top)
    const naturalHeight = error.scrollHeight;
    // eslint-disable-next-line no-unused-expressions
    error.offsetHeight;
    error.style.maxHeight = `${naturalHeight}px`;
    error.style.opacity = '1';

    // Auto-dismiss after 5s
    setTimeout(() => {
      error.classList.add('cart-item__error--hiding');
      error.addEventListener('transitionend', (e) => {
        if (e.target === error && e.propertyName === 'max-height') error.remove();
      });
    }, 5000);
  }

  /**
   * @param {number} line - 1-based line index
   * @returns {Promise<void>}
   */
  #animateRemoval(line) {
    const item = this.#lineElement(line);
    if (!item) return Promise.resolve();

    // Lock the current height so CSS can transition from it to 0
    item.style.maxHeight = `${item.offsetHeight}px`;

    // Force reflow then add class to trigger all transitions
    // eslint-disable-next-line no-unused-expressions
    item.offsetHeight;
    item.classList.add('cart-item--removing');

    // The section refresh waits on this promise, so it must resolve even when `transitionend` never
    // fires — a throttled or backgrounded renderer simply does not run the transition, and the cart
    // would then stay frozen on stale contents forever. The timeout is the safety net, whichever
    // comes first wins.
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        item.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      /** @param {TransitionEvent} event */
      const onTransitionEnd = (event) => {
        if (event.target === item && event.propertyName === 'max-height') done();
      };

      const timer = setTimeout(done, REMOVAL_ANIMATION_TIMEOUT);
      item.addEventListener('transitionend', onTransitionEnd);
    });
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
    if (event.target === this) return;

    updateCartCount(event.detail?.data?.itemCount);

    // Skip morph for CartAddEvent when hosted in a drawer — the drawer component handles it with animation
    if (event instanceof CartAddEvent && this.isDrawer) return;

    const html = event.detail?.data?.sections?.[this.sectionId];
    if (html) {
      morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
    } else {
      // No pre-rendered section in the payload: the round trip is ours, so it needs the flag. An
      // add from the cross-sell or from a Nosto placement lands here on an empty cart, where the
      // whole section flips to the full cart.
      this.#setBusy(true);
      sectionRenderer.renderSection(this.sectionId, { cache: false }).finally(() => this.#setBusy(false));
    }

    this.#restoreScrollLock();
  };

  /**
   * Flags the whole cart as being updated. Purely a styling hook: the CSS that reacts to it lives
   * with the markup it dims (`sections/main-cart.liquid` for the recap and the sticky bar,
   * `snippets/cart-line-item.liquid` for a single line).
   *
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.toggleAttribute('data-updating', busy);
    this.setAttribute('aria-busy', String(busy));
  }

  #restoreScrollLock() {
    const dialog = this.closest('dialog');
    if (dialog?.open) {
      dialog.setAttribute('scroll-lock', '');
    }
  }

  get sectionId() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      throw new Error('Section id missing on cart-items-component');
    }

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
